import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Zap,
  Calendar,
  GanttChart as GanttIcon,
  Folder,
  Hash,
  ArrowUpDown,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isToday,
  isSameMonth,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  isWeekend,
  startOfDay,
  differenceInDays,
} from 'date-fns';
import { it } from 'date-fns/locale';
import type {
  Task,
  GroupingState,
  SortingState,
  Priority,
} from '@/types';
import {
  ViewToolbar,
  ShowCompletedToggle,
  NavButtons,
  ViewToggleGroup,
  MultiGroupingSelector,
  MultiSortingSelector,
  MultiFilterSelector,
  type BreadcrumbItem,
} from '@/components/shared/view-toolbar';
import {
  StatusSelect,
} from '@/components/shared/inline-editors';
import clsx from 'clsx';
import {
  getTaskGroupingOptions,
  createDefaultGroupingState,
  getTaskSortingOptions,
  createDefaultSortingState,
} from '@/utils/grouping';
import { getAllFilterOptions } from '@/utils/filterOptions';
import { useProfilesContext } from '@/context/ProfilesContext';
import { normalizeStatus, isCompletedStatus, TASK_STATUS } from '@/constants/taskStatuses';
import { Filter } from 'lucide-react';
import { useViewData } from '@/hooks/useViewData';
import { useTaskActions } from '@/hooks/useTaskActions';
import { useAppStore } from '@/store/appStore';

type ZoomLevel = 'day' | 'week' | 'month' | 'quarter';

export function GanttChart() {
  const {
    tasks,
    filters,
    grouping,
    sorting,
    viewMode,
    uniqueValues,
    currentPath,
    loading,
    onTaskClick,
    onFilterChange,
    onGroupingChange,
    onSortingChange,
    onNavigate,
    onViewModeChange,
    containersMap,
  } = useViewData();
  // Gli URL usano gli id degli spazi: senza risolverli il titolo e le briciole
  // di pane mostrano un UUID al posto del nome (come già fa TaskList).
  const getNameForId = useCallback((id: string): string => {
    return containersMap?.get(id)?.name ?? id;
  }, [containersMap]);

  const { handleUpdateTask: onUpdateTask } = useTaskActions();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tasksRef = useRef<HTMLDivElement>(null);
  const [hoveredTask, setHoveredTask] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('day');
  const [localGrouping, setLocalGrouping] = useState<GroupingState>(
    () => grouping || createDefaultGroupingState()
  );
  const [localSorting, setLocalSorting] = useState<SortingState>(
    () => sorting || createDefaultSortingState()
  );

  // Grouping handlers
  const handleGroupingChange = useCallback(
    (newGrouping: GroupingState) => {
      setLocalGrouping(newGrouping);
      onGroupingChange(newGrouping);
    },
    [onGroupingChange]
  );

  // Sorting handlers
  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setLocalSorting(newSorting);
      onSortingChange(newSorting);
    },
    [onSortingChange]
  );

  // Filter options with consistent ordering
  const { resolveDisplayName } = useProfilesContext();
  const filterOptions = useMemo(() => {
    return getAllFilterOptions(
      uniqueValues.statuses,
      uniqueValues.assignees,
      uniqueValues.tags,
      uniqueValues.priorities as Priority[],
      resolveDisplayName,
    );
  }, [uniqueValues, resolveDisplayName]);

  // Filter tasks with dates (keeping the original logic for now)
  const tasksWithDates = useMemo(() => {
    return tasks.filter((task) => task.startDate || task.dueDate);
  }, [tasks]);

  // Compute breadcrumbs (exclude current page)
  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [];

    if (!currentPath) {
      return items;
    }

    const pathParts = currentPath.split('/');

    // If we're on a list (3 parts), show space and folder breadcrumbs
    if (pathParts.length === 3) {
      // Space breadcrumb
      items.push({
        label: getNameForId(pathParts[0]),
        onClick: onNavigate ? () => onNavigate(pathParts[0]) : undefined,
      });
      // Folder breadcrumb
      items.push({
        label: getNameForId(pathParts[1]),
        onClick: onNavigate ? () => onNavigate(`${pathParts[0]}/${pathParts[1]}`) : undefined,
      });
    } else if (pathParts.length === 2) {
      // If we're on a folder or list without folder, show only space breadcrumb
      items.push({
        label: getNameForId(pathParts[0]),
        onClick: onNavigate ? () => onNavigate(pathParts[0]) : undefined,
      });
    }
    // If we're on a space (1 part) or root, no breadcrumbs needed

    return items;
  }, [currentPath, onNavigate, getNameForId]);

  // Compute toolbar title and icon based on navigation context
  const { toolbarIcon, toolbarTitle, toolbarSubtitle } = useMemo(() => {
    const taskCount = `${tasksWithDates.length} task con date`;
    if (!currentPath) {
      return {
        toolbarIcon: <GanttIcon className="w-4 h-4 text-accent-violet" />,
        toolbarTitle: 'Tutti i task',
        toolbarSubtitle: taskCount,
      };
    }

    const pathParts = currentPath.split('/');
    const lastPart = getNameForId(pathParts[pathParts.length - 1]);

    // Determine type based on path length and context
    if (pathParts.length === 3) {
      // Likely a list (space/folder/list)
      return {
        toolbarIcon: <Hash className="w-4 h-4 text-accent-emerald" />,
        toolbarTitle: lastPart,
        toolbarSubtitle: taskCount,
      };
    } else if (pathParts.length === 2) {
      // Could be folder or list without folder
      return {
        toolbarIcon: <Folder className="w-4 h-4 text-accent-blue" />,
        toolbarTitle: lastPart,
        toolbarSubtitle: taskCount,
      };
    } else {
      // Single part - space
      return {
        toolbarIcon: <Zap className="w-4 h-4 text-accent-amber" />,
        toolbarTitle: lastPart,
        toolbarSubtitle: taskCount,
      };
    }
  }, [currentPath, tasksWithDates.length, getNameForId]);

  // Always start from current date - user can navigate to other periods
  const [currentDate, setCurrentDate] = useState(() => new Date());

  // Calculate date range
  const { dateRange, dayWidth, totalWidth } = useMemo(() => {
    let start: Date;
    let end: Date;
    let dayWidth: number;

    if (zoomLevel === 'day') {
      start = startOfMonth(currentDate);
      end = endOfMonth(currentDate);
      dayWidth = 40;
    } else if (zoomLevel === 'week') {
      start = startOfWeek(subWeeks(currentDate, 2), { locale: it });
      end = endOfWeek(addWeeks(currentDate, 6), { locale: it });
      dayWidth = 20;
    } else if (zoomLevel === 'month') {
      start = startOfMonth(subMonths(currentDate, 1));
      end = endOfMonth(addMonths(currentDate, 4));
      dayWidth = 8;
    } else {
      start = startOfMonth(subMonths(currentDate, 2));
      end = endOfMonth(addMonths(currentDate, 10));
      dayWidth = 3;
    }

    const days = eachDayOfInterval({ start, end });
    const totalWidth = days.length * dayWidth;

    return { dateRange: { start, end, days }, dayWidth, totalWidth };
  }, [currentDate, zoomLevel]);

  // Virtualizer for task rows
  const rowVirtualizer = useVirtualizer({
    count: tasksWithDates.length,
    getScrollElement: () => tasksRef.current,
    estimateSize: () => 40,
    overscan: 10,
  });

  // Navigate
  const goToPrevious = useCallback(() => {
    if (zoomLevel === 'day') {
      setCurrentDate((prev) => subMonths(prev, 1));
    } else if (zoomLevel === 'week') {
      setCurrentDate((prev) => subWeeks(prev, 4));
    } else if (zoomLevel === 'month') {
      setCurrentDate((prev) => subMonths(prev, 3));
    } else {
      setCurrentDate((prev) => subMonths(prev, 6));
    }
  }, [zoomLevel]);

  const goToNext = useCallback(() => {
    if (zoomLevel === 'day') {
      setCurrentDate((prev) => addMonths(prev, 1));
    } else if (zoomLevel === 'week') {
      setCurrentDate((prev) => addWeeks(prev, 4));
    } else if (zoomLevel === 'month') {
      setCurrentDate((prev) => addMonths(prev, 3));
    } else {
      setCurrentDate((prev) => addMonths(prev, 6));
    }
  }, [zoomLevel]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Scroll to today on mount
  useEffect(() => {
    if (scrollRef.current) {
      const todayIndex = dateRange.days.findIndex((d) => isToday(d));
      if (todayIndex >= 0) {
        const scrollPosition = todayIndex * dayWidth - 200;
        scrollRef.current.scrollLeft = Math.max(0, scrollPosition);
      }
    }
  }, [dateRange.days, dayWidth]);

  // Sync horizontal scroll (no-op since scrollRef scrolls itself)
  const handleScroll = useCallback((_e: React.UIEvent<HTMLDivElement>) => {}, []);

  // Sync vertical scroll: when task list scrolls, keep timeline bars in sync
  const handleTasksScroll = useCallback(() => {
    if (scrollRef.current && tasksRef.current) {
      scrollRef.current.scrollTop = tasksRef.current.scrollTop;
    }
  }, []);

  // Get task bar position
  const getTaskBar = useCallback(
    (task: Task) => {
      const start = task.startDate || task.dueDate;
      const end = task.dueDate || task.startDate;

      if (!start || !end) return null;

      const taskStart = startOfDay(new Date(start));
      const taskEnd = startOfDay(new Date(end));
      const rangeStart = startOfDay(dateRange.days[0]);
      const rangeEnd = startOfDay(dateRange.days[dateRange.days.length - 1]);

      // Skip if entirely outside visible range
      if (taskEnd < rangeStart || taskStart > rangeEnd) return null;

      const startOffset = differenceInDays(taskStart, rangeStart);
      const endOffset = differenceInDays(taskEnd, rangeStart);

      const actualStartIndex = Math.max(0, startOffset);
      const actualEndIndex = Math.min(dateRange.days.length - 1, endOffset);
      const duration = Math.max(1, actualEndIndex - actualStartIndex + 1);

      return {
        left: actualStartIndex * dayWidth,
        width: duration * dayWidth - 4,
        isPartial: startOffset < 0 || endOffset >= dateRange.days.length,
      };
    },
    [dateRange.days, dayWidth]
  );

  const getStatusColor = (status: string) => {
    const s = normalizeStatus(status);
    if (isCompletedStatus(s)) return 'bg-accent-emerald';
    if (s === TASK_STATUS.IN_PROGRESS) return 'bg-accent-blue';
    if (s === TASK_STATUS.BLOCKED) return 'bg-accent-rose';
    return 'bg-accent-violet';
  };

  // Group dates by month for header
  const groupedDates = useMemo(() => {
    const months: { date: Date; days: Date[] }[] = [];
    let currentMonth: Date | null = null;
    let currentDays: Date[] = [];

    dateRange.days.forEach((day) => {
      if (!currentMonth || !isSameMonth(day, currentMonth)) {
        if (currentMonth) {
          months.push({ date: currentMonth, days: currentDays });
        }
        currentMonth = day;
        currentDays = [day];
      } else {
        currentDays.push(day);
      }
    });

    if (currentMonth) {
      months.push({ date: currentMonth, days: currentDays });
    }

    return months;
  }, [dateRange.days]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary animate-fade-in">
      {/* Toolbar */}
      <ViewToolbar
        icon={toolbarIcon}
        title={toolbarTitle}
        subtitle={toolbarSubtitle}
        breadcrumbs={breadcrumbs}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        showViewSwitcher={!!onViewModeChange}
        onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
        leftContent={
          <div className="flex items-center flex-wrap min-w-0 gap-3">
            {/* Navigation */}
            <NavButtons
              onPrevious={goToPrevious}
              onNext={goToNext}
              onToday={goToToday}
              todayLabel="Oggi"
            />

            <span className="text-[12px] font-semibold text-text-primary capitalize">
              {format(currentDate, 'MMMM yyyy', { locale: it })}
            </span>

            {filters && onFilterChange && (
              <div className="flex items-center gap-2 gap-y-2 flex-wrap">
                <MultiFilterSelector
                  filters={{
                    status: filters.status || [],
                    assignees: filters.assignees || [],
                    tags: filters.tags || [],
                    priority: filters.priority || [],
                  }}
                  onFilterChange={onFilterChange}
                  availableOptions={filterOptions}
                  icon={<Filter className="w-3.5 h-3.5 text-text-tertiary" />}
                />
                <MultiGroupingSelector
                  grouping={localGrouping}
                  onGroupingChange={handleGroupingChange}
                  availableOptions={getTaskGroupingOptions()}
                  icon={<GanttIcon className="w-3.5 h-3.5 text-text-tertiary" />}
                />
                <MultiSortingSelector
                  sorting={localSorting}
                  onSortingChange={handleSortingChange}
                  availableOptions={getTaskSortingOptions()}
                  icon={<ArrowUpDown className="w-3.5 h-3.5 text-text-tertiary" />}
                />
                <ShowCompletedToggle
                  showCompleted={filters.showCompleted}
                  onToggle={(show) => onFilterChange({ showCompleted: show })}
                />
              </div>
            )}
          </div>
        }
        rightContent={
          <ViewToggleGroup
            value={zoomLevel}
            onChange={(v) => setZoomLevel(v as ZoomLevel)}
            options={[
              { value: 'quarter', icon: <span className="text-[10px]">T</span>, label: 'Trimestre' },
              { value: 'month', icon: <span className="text-[10px]">M</span>, label: 'Mesi' },
              { value: 'week', icon: <span className="text-[10px]">S</span>, label: 'Settimane' },
              { value: 'day', icon: <span className="text-[10px]">G</span>, label: 'Giorni' },
            ]}
          />
        }
      />

      {/* Gantt Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Task Names Column */}
        <div className="w-[420px] shrink-0 border-r border-border-primary bg-surface-secondary flex flex-col">
          {/* Header */}
          <div className="h-14 px-4 flex items-end pb-2.5 border-b border-border-primary bg-surface-tertiary/30">
            <span className="text-[10px] uppercase tracking-wider text-text-quaternary font-semibold">
              Task
            </span>
          </div>

          {/* Task List - Virtualized */}
          <div ref={tasksRef} className="flex-1 overflow-y-auto" onScroll={handleTasksScroll}>
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const task = tasksWithDates[virtualRow.index];
                const handleStatusChange = (status: string) => {
                  if (onUpdateTask) onUpdateTask(task.id, { status });
                };
                return (
                  <div
                    // Guard against occasional duplicate task IDs in the dataset
                    key={`${task.id}-${virtualRow.index}`}
                    onMouseEnter={() => setHoveredTask(task.id)}
                    onMouseLeave={() => setHoveredTask(null)}
                    className={clsx(
                      'absolute top-0 left-0 w-full h-10 px-3 flex items-center gap-1.5 border-b border-border-primary/30 transition-colors overflow-hidden',
                      hoveredTask === task.id ? 'bg-surface-hover' : 'hover:bg-surface-hover/50'
                    )}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {/* Status */}
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                      <StatusSelect
                        value={task.status}
                        onChange={handleStatusChange}
                        size="sm"
                        showLabel={false}
                        disabled={!onUpdateTask}
                      />
                    </div>
                    {/* Task Name */}
                    <div
                      className="flex-1 min-w-0 overflow-hidden cursor-pointer"
                      onClick={() => onTaskClick?.(task)}
                    >
                      <span
                        className="text-[12px] text-text-primary truncate block font-medium hover:text-accent-violet transition-colors"
                        title={task.name}
                      >
                        {task.name}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden" ref={scrollRef} onScroll={handleScroll}>
          <div style={{ width: totalWidth }} className="min-h-full flex flex-col">
            {/* Date Header */}
            <div className="h-14 border-b border-border-primary bg-surface-secondary/30 sticky top-0 z-10 shrink-0">
              {/* Month Row */}
              {zoomLevel !== 'day' && (
                <div className="h-7 flex border-b border-border-primary/50">
                  {groupedDates.map(({ date, days }) => (
                    <div
                      key={format(date, 'yyyy-MM')}
                      className="flex items-center justify-center text-[10px] font-semibold text-text-secondary capitalize border-r border-border-primary/30"
                      style={{ width: days.length * dayWidth }}
                    >
                      {format(date, 'MMMM yyyy', { locale: it })}
                    </div>
                  ))}
                </div>
              )}

              {/* Days Row */}
              <div className={clsx('flex', zoomLevel !== 'day' ? 'h-7' : 'h-14')}>
                {dateRange.days.map((day, i) => {
                  const today = isToday(day);
                  const weekend = isWeekend(day);

                  return (
                    <div
                      key={i}
                      className={clsx(
                        'flex flex-col items-center justify-end pb-1.5 border-r border-border-primary/30',
                        today && 'bg-accent-violet/10',
                        weekend && !today && 'bg-surface-tertiary/30'
                      )}
                      style={{ width: dayWidth }}
                    >
                      {zoomLevel === 'day' && (
                        <>
                          <span className="text-[10px] text-text-quaternary uppercase font-medium">
                            {format(day, 'EEE', { locale: it })}
                          </span>
                          <span
                            className={clsx(
                              'text-[12px] font-semibold tabular-nums mt-0.5',
                              today ? 'text-accent-violet' : 'text-text-tertiary'
                            )}
                          >
                            {format(day, 'd')}
                          </span>
                        </>
                      )}
                      {zoomLevel === 'week' && format(day, 'E', { locale: it }) === 'lun' && (
                        <span className="text-[10px] text-text-tertiary tabular-nums font-medium">
                          {format(day, 'd', { locale: it })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Task Bars */}
            <div className="relative flex-1">
              {/* Today Line */}
              {dateRange.days.some((d) => isToday(d)) && (
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-accent-violet z-20"
                  style={{
                    left: dateRange.days.findIndex((d) => isToday(d)) * dayWidth + dayWidth / 2 - 1,
                    boxShadow: '0 0 12px rgba(139,92,246,0.4)',
                  }}
                />
              )}

              {/* Grid Lines */}
              <div className="absolute inset-0 flex pointer-events-none">
                {dateRange.days.map((day, i) => (
                  <div
                    key={i}
                    className={clsx(
                      'h-full border-r border-border-primary/15',
                      isWeekend(day) && 'bg-surface-tertiary/10'
                    )}
                    style={{ width: dayWidth }}
                  />
                ))}
              </div>

              {/* Virtualized Task Rows */}
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const task = tasksWithDates[virtualRow.index];
                  const bar = getTaskBar(task);
                  const statusColor = getStatusColor(task.status);
                  const isHovered = hoveredTask === task.id;

                  return (
                    <div
                      key={task.id}
                      className="absolute top-0 left-0 w-full h-10 border-b border-border-primary/15"
                      style={{
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                      onMouseEnter={() => setHoveredTask(task.id)}
                      onMouseLeave={() => setHoveredTask(null)}
                    >
                      {bar && (
                        <div
                          onClick={() => onTaskClick?.(task)}
                          className={clsx(
                            'absolute top-2 h-6 rounded-md cursor-pointer transition-all flex items-center overflow-hidden',
                            statusColor,
                            isHovered
                              ? 'brightness-110 shadow-lg scale-y-110 z-10'
                              : 'hover:brightness-105'
                          )}
                          style={{
                            left: bar.left + 2,
                            width: Math.max(bar.width, 24),
                          }}
                        >
                          {bar.width > 80 && (
                            <span className="px-2 text-[10px] font-semibold text-white truncate">
                              {task.name}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Loading State */}
              {loading && tasksWithDates.length === 0 && (
                <div className="p-4 animate-fade-in">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 py-2">
                      <div className="w-[200px] h-3.5 rounded bg-surface-hover animate-pulse" style={{ width: `${120 + Math.random() * 100}px` }} />
                      <div className="flex-1 h-6 rounded bg-surface-hover animate-pulse" style={{ marginLeft: `${i * 40}px`, width: `${80 + Math.random() * 120}px` }} />
                    </div>
                  ))}
                </div>
              )}
              {/* Empty State */}
              {!loading && tasksWithDates.length === 0 && (
                <div className="flex items-center justify-center h-64 text-text-quaternary">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-tertiary flex items-center justify-center">
                      <Calendar className="w-8 h-8 text-text-quaternary" />
                    </div>
                    <p className="text-[13px] font-medium text-text-secondary">
                      Nessun task con date
                    </p>
                    <p className="text-[11px] text-text-quaternary mt-1">
                      Aggiungi date ai task per visualizzarli nel Gantt
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
